import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiService } from '@/services/api';
import { useCurrency } from '@/contexts/CurrencyContext';
import { isSuperAdmin } from '@/utils/roleUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Icon from '@/components/atoms/Icon/Icon';
import TeamManagement from '@/components/organisms/TeamManagement';
import './Dashboard.scss';


interface DashboardStats {
  activeEvents: number;
  pendingOrders: number;
  lowStockDrinks: number;
  lowStockFoods: number;
  todayRevenue: number;
  allTimeRevenue: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [stats, setStats] = useState<DashboardStats>({
    activeEvents: 0,
    pendingOrders: 0,
    lowStockDrinks: 0,
    lowStockFoods: 0,
    todayRevenue: 0,
    allTimeRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isOrganizer = user?.role === 'organizer';
  const isSuperAdminUser = isSuperAdmin(user);

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardStats = async () => {
      if (!isOrganizer && !isSuperAdminUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel
        const [eventsData, allOrdersData, drinksData, foodsData] = await Promise.all([
          apiService.getMyManagedEvents({ active_only: true }),
          apiService.getOrders(), // Get all orders to calculate both pending and revenue
          apiService.getDrinks(),
          apiService.getFoods()
        ]);

        // Calculate active events
        const activeEvents = eventsData.length;

        // Calculate pending orders
        const pendingOrders = allOrdersData.filter(order => order.status === 'paid').length;

        // Calculate low stock items (drinks + foods)
        const lowStockDrinks = drinksData.filter(drink => 
          drink.stock_quantity <= drink.min_stock_threshold
        ).length;
        const lowStockFoods = foodsData.filter(food => 
          food.stock_quantity <= food.min_stock_threshold
        ).length;

        const lowStockItems = lowStockDrinks + lowStockFoods;

        // Calculate today's revenue (orders with status 'completed' from today)
        const today = new Date().toISOString().split('T')[0];
        const todayOrders = allOrdersData.filter(order => 
          order.status === 'paid' && 
          order.created_at.startsWith(today)
        );
        const todayRevenue = todayOrders.reduce((total, order) => {
          return total + (order.total_amount || 0);
        }, 0);

        // Calculate all time revenue (orders with status 'completed')
        const allTimeOrders = allOrdersData.filter(order => 
          order.status === 'paid'
        );
        const allTimeRevenue = allTimeOrders.reduce((total, order) => {
          return total + (order.total_amount || 0);
        }, 0);

        setStats({
          activeEvents,
          pendingOrders,
          lowStockDrinks,
          lowStockFoods,
          todayRevenue,
          allTimeRevenue
        });

      } catch (err: any) {
        console.error('Error fetching dashboard stats:', err);
        setError(err.message || t('errors.general'));
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [isOrganizer, isSuperAdminUser, t]);

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard__header">
          <h1 className="dashboard__title">{t('dashboard.title')}</h1>
          <p className="dashboard__subtitle">{t('dashboard.welcome')}</p>
        </div>
        
        {isSuperAdminUser ? (
          // Super Admin Dashboard
          <div className="dashboard__superadmin">
            {/* Management Section */}
            <div className="dashboard__management-grid">
              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="eventos" size={40} alt={t('dashboard.events.title')} /> 
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.events.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.events.description')}</p>
                  <Link to="/events/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.events.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="casa" size={40} alt={t('dashboard.locals.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.locals.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.locals.description')}</p>
                  <Link to="/locals" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.locals.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="inventario" size={40} alt={t('dashboard.products.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.products.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.products.description')}</p>
                  <Link to="/products" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.products.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="grafico" size={40} alt={t('dashboard.stock.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.stock.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.stock.description')}</p>
                  <Link to="/warehouse" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.stock.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="vasos" size={40} alt={t('dashboard.drinks.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.drinks.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.drinks.description')}</p>
                  <Link to="/drinks/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.drinks.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="cubiertos" size={40} alt={t('dashboard.foods.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.foods.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.foods.description')}</p>
                  <Link to="/foods/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.foods.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="pedidos" size={40} alt={t('dashboard.orders.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.orders.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.orders.description')}</p>
                  <Link to="/orders/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.orders.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="equipo" size={40} alt={t('dashboard.team.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.team.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.team.description')}</p>
                  <Link to="/team/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.team.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="configuraciones" size={40} alt={t('navigation.globalSettings')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('navigation.globalSettings')}</h3>
                  <p className="dashboard__card-description">{t('admin.globalSettings.description')}</p>
                  <Link to="/admin/global-settings" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('navigation.globalSettings')}
                    </Button>
                  </Link>
                </div>
              </Card>
              
              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="signo-dolar" size={40} alt={t('navigation.countryTaxRates')} /> 
                  </div>
                  <h3 className="dashboard__card-title">{t('navigation.countryTaxRates')}</h3>
                  <p className="dashboard__card-description">{t('admin.countryTaxRates.description')}</p>
                  <Link to="/admin/country-tax-rates" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('navigation.countryTaxRates')}
                    </Button>
                  </Link>
                </div>
              </Card>
              
              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="equipo" size={40} alt={t('navigation.users')} /> 
                  </div>
                  <h3 className="dashboard__card-title">{t('navigation.users')}</h3>
                  <p className="dashboard__card-description">{t('admin.users.description')}</p>
                  <Link to="/admin/users" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('navigation.users')}
                    </Button>
                  </Link>
                </div>
              </Card>
              
              
            </div>
          </div>
        ) : isOrganizer ? (
          // Organizer Dashboard
          <div className="dashboard__organizer">
            {/* Quick Stats Section */}
            <div className="dashboard__stats-section">
              <h2 className="dashboard__stats-title">{t('dashboard.quickStats')}</h2>
              
              {error && (
                <Card variant="elevated" padding="md" className="dashboard__error">
                  <p>{error}</p>
                </Card>
              )}
              
              <div className="dashboard__stats-grid">
                <Card variant="elevated" padding="md" className="dashboard__stat-card">
                  <div className="dashboard__stat-content">
                    <div className="dashboard__stat-icon">
                      <Icon name="eventos" size={40} alt={t('dashboard.stats.activeEvents')} />
                    </div>
                    <div className="dashboard__stat-info">
                      <span className="dashboard__stat-number">
                        {loading ? '...' : stats.activeEvents}
                      </span>
                      <span className="dashboard__stat-label">{t('dashboard.stats.activeEvents')}</span>
                    </div>
                  </div>
                </Card>
                
                <Card variant="elevated" padding="md" className="dashboard__stat-card">
                  <div className="dashboard__stat-content">
                    <div className="dashboard__stat-icon">
                      <Icon name="pedidos" size={40} alt={t('dashboard.stats.pendingOrders')} /> 
                    </div>
                    <div className="dashboard__stat-info">
                      <span className="dashboard__stat-number">
                        {loading ? '...' : stats.pendingOrders}
                      </span>
                      <span className="dashboard__stat-label">{t('dashboard.stats.pendingOrders')}</span>
                    </div>
                  </div>
                </Card>
                
                <Card variant="elevated" padding="md" className="dashboard__stat-card">
                  <div className="dashboard__stat-content">
                    <div className="dashboard__stat-icon">
                      <Icon name="vasos" size={40} alt={t('dashboard.stats.lowStockItems')} /> 
                    </div>
                    <div className="dashboard__stat-info">
                      <span className="dashboard__stat-number">
                        {loading ? '...' : stats.lowStockDrinks}
                      </span>
                      <span className="dashboard__stat-label">{t('dashboard.stats.lowStockItems')}</span>
                    </div>
                  </div>
                </Card>

                <Card variant="elevated" padding="md" className="dashboard__stat-card">
                  <div className="dashboard__stat-content">
                    <div className="dashboard__stat-icon">
                      <Icon name="cubiertos" size={40} alt={t('dashboard.stats.lowStockItems')} /> 
                    </div>
                    <div className="dashboard__stat-info">
                      <span className="dashboard__stat-number">
                        {loading ? '...' : stats.lowStockFoods}
                      </span>
                      <span className="dashboard__stat-label">{t('dashboard.stats.lowStockItems')}</span>
                    </div>
                  </div>
                </Card>
                
                <Card variant="elevated" padding="md" className="dashboard__stat-card">
                  <div className="dashboard__stat-content">
                    <div className="dashboard__stat-icon">
                      <Icon name="signo-dolar" size={40} alt={t('dashboard.stats.todayRevenue')} /> 
                    </div>
                    <div className="dashboard__stat-info">
                      <span className="dashboard__stat-number">
                        {loading ? '...' : formatPrice(stats.todayRevenue)}
                      </span>
                      <span className="dashboard__stat-label">{t('dashboard.stats.todayRevenue')}</span>
                    </div>
                  </div>
                </Card>

                <Card variant="elevated" padding="md" className="dashboard__stat-card">
                  <div className="dashboard__stat-content">
                    <div className="dashboard__stat-icon">
                      <Icon name="signo-dolar" size={40} alt={t('dashboard.stats.allTimeRevenue')} /> 
                    </div>
                    <div className="dashboard__stat-info">
                      <span className="dashboard__stat-number">
                        {loading ? '...' : formatPrice(stats.allTimeRevenue)}
                      </span>
                      <span className="dashboard__stat-label">{t('dashboard.stats.allTimeRevenue')}</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            
            {/* Management Section */}
            <div className="dashboard__management-grid">
              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="eventos" size={40} alt={t('dashboard.events.title')} /> 
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.events.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.events.description')}</p>
                  <Link to="/events/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.events.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="casa" size={40} alt={t('dashboard.locals.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.locals.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.locals.description')}</p>
                  <Link to="/locals" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.locals.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="inventario" size={40} alt={t('dashboard.products.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.products.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.products.description')}</p>
                  <Link to="/products" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.products.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="grafico" size={40} alt={t('dashboard.stock.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.stock.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.stock.description')}</p>
                  <Link to="/warehouse" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.stock.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>

              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="vasos" size={40} alt={t('dashboard.drinks.title')} />
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.drinks.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.drinks.description')}</p>
                  <Link to="/drinks/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.drinks.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>
              
              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="cubiertos" size={40} alt={t('dashboard.foods.title')} /> 
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.foods.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.foods.description')}</p>
                  <Link to="/foods/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.foods.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>
              
              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="pedidos" size={40} alt={t('dashboard.orders.title')} /> 
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.orders.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.orders.description')}</p>
                  <Link to="/orders/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.orders.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>
              
              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="equipo" size={40} alt={t('dashboard.team.title')} /> 
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.team.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.team.description')}</p>
                  <Link to="/team/manage" className="dashboard__card-link">
                    <Button variant="primary" className="dashboard__card-button">
                      {t('dashboard.team.manage')}
                    </Button>
                  </Link>
                </div>
              </Card>
              
              <Card variant="elevated" padding="lg" className="dashboard__management-card">
                <div className="dashboard__card-content">
                  <div className="dashboard__card-icon">
                    <Icon name="grafico" size={40} alt={t('dashboard.analytics.title')} /> 
                  </div>
                  <h3 className="dashboard__card-title">{t('dashboard.analytics.title')}</h3>
                  <p className="dashboard__card-description">{t('dashboard.analytics.description')}</p>
                  <Button variant="secondary" className="dashboard__card-button" disabled>
                    {t('dashboard.analytics.comingSoon')}
                  </Button>
                </div>
              </Card>
            </div>

            
          </div>
        ) : (
          // Customer Dashboard
          <div className="dashboard__customer">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg">
              <Card variant="elevated" padding="lg">
                <h3>{t('dashboard.customer.events')}</h3>
                <p>{t('dashboard.customer.eventsDescription')}</p>
                <Link to="/events">
                  <Button variant="primary" size="sm">
                    {t('dashboard.customer.browseEvents')}
                  </Button>
                </Link>
              </Card>
              
              <Card variant="elevated" padding="lg">
                <h3>{t('dashboard.customer.orders')}</h3>
                <p>{t('dashboard.customer.ordersDescription')}</p>
                <Link to="/orders">
                  <Button variant="primary" size="sm">
                    {t('dashboard.customer.viewOrders')}
                  </Button>
                </Link>
              </Card>
              
              <Card variant="elevated" padding="lg">
                <h3>{t('dashboard.customer.profile')}</h3>
                <p>{t('dashboard.customer.profileDescription')}</p>
                <Link to="/profile">
                  <Button variant="primary" size="sm">
                    {t('dashboard.customer.editProfile')}
                  </Button>
                </Link>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 